import { motion } from 'framer-motion';
import { CheckCircle2, Info, Users } from 'lucide-react';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import LoadingComponent from '../shared/components/LoadingComponent';
import FooterNavigation from '../shared/components/Navigation';
import Pagination from '../shared/components/Pagination';
import { useProviderData } from '../shared/hooks/useProviderSelection';
import ProviderDisplay from '../layouts/centered/components/ProviderDisplay';
import ImmersiveProviderDisplay from '../layouts/immersive/components/ImmersiveProviderDisplay';
import { useLayoutContext } from '../shared/hooks/useLayoutContext';
import SelectionHeader from './SelectionHeader';
import type {
  Location,
  Provider,
  Service,
  ServiceProviderMap
} from './types';

interface NoProvidersMessageProps {
  colors?: {
    titleText?: string;
    cardBackground?: string;
    cardBorder?: string;
    cardShadow?: string;
  };
}

const NoProvidersMessage: FC<NoProvidersMessageProps> = ({ colors = {} }) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Default colors if not provided
  const titleText = colors.titleText || '#DDB889';
  const cardBackground = colors.cardBackground || 'rgba(0, 0, 0, 0.95)';
  const cardBorder = colors.cardBorder || '#DDB88919';
  const cardShadow = colors.cardShadow || 'rgba(221,184,137,0.07)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-flex lp-flex-col lp-items-center lp-justify-center lp-min-h-[400px] lp-text-center lp-p-8"
    >
      <div
        className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-8 lp-max-w-lg
          lp-transition-all lp-duration-700"
        style={{
          backgroundColor: cardBackground,
          borderColor: `${cardBorder}`,
          boxShadow: `0 0 15px ${cardShadow}`
        }}
      >
        <CheckCircle2
          className="lp-w-12 lp-h-12 lp-mx-auto lp-mb-4"
          style={{ color: titleText }}
        />
        <h3
          className="lp-text-xl lp-font-medium lp-mb-3"
          style={{ color: titleText }}
        >
          {t('providers.serviceMarkedAsCompleted')} {/* CHANGED: "Service Marked as Completed" */}
        </h3>
        <p className="lp-text-gray-300 lp-mb-4">
          {t('providers.noProvidersAvailableDescription')} {/* CHANGED: "No providers are available at the moment..." */}
        </p>
        <div
          className="lp-flex lp-items-center lp-justify-center lp-gap-2 lp-text-sm"
          style={{ color: `${titleText}CC` }}
        >
          <Info size={16} />
          <span>{t('providers.canProceedToNextStep')}</span> {/* CHANGED: "You can proceed to the next step" */}
        </div>
      </div>
    </motion.div>
  );
};

interface ProviderStepProps {
  selectedProvider: Provider | null;
  selectedProviders?: Provider[];
  setSelectedProvider: (provider: Provider | Provider[] | null) => void;
  onNext: () => void;
  onPrev: () => void;
  selectedServices: Service[];
  selectedLocation: Location | null;
  setServiceProviderMap: (value: ServiceProviderMap | ((prev: ServiceProviderMap) => ServiceProviderMap)) => void;
  serviceProviderMap: ServiceProviderMap;
  nodeData?: any;
}

const ProviderStep: React.FC<ProviderStepProps> = ({
  selectedProvider: externalSelectedProvider,
  setSelectedProvider: externalSetSelectedProvider,
  onNext,
  onPrev,
  selectedServices,
  selectedLocation,
  setServiceProviderMap,
  serviceProviderMap,
  selectedProviders: externalSelectedProviders = [],
  nodeData = null,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE
  const { layoutType } = useLayoutContext();

  // Set default nodeData if none is provided
  const defaultNodeData = {
    label: t('providers.staffMembers'), // CHANGED: "Staff Members"
    settings: {
      showPhotos: true,
      showAvailability: true,
      displayStyle: "cards",
      itemsPerRow: 3,
      numberRows: 2,
      colors: {
          cardBackground: 'rgba(0, 0, 0, 0.95)',
          cardBorder: '#DDB88919',
          cardBorderSelected: "#22c55e49",
          cardShadow: 'rgba(221,184,137,0.07)',
          cardShadowSelected: "rgba(34,197,94,0.25)",
          titleText: '#DDB889',
          subtitleText: '#9CA3AF',
          iconColor: '#DDB889B2',
          selectionBackground: "#22c55e",
          selectionIcon: "#FFFFFF"
      }
    }
  };

  const effectiveNodeData = nodeData || defaultNodeData;
  const colors = effectiveNodeData?.settings?.colors || {};

  const [activeService, setActiveService] = React.useState<string | null>(
    selectedServices?.[0]?.id ?? null
  );

  // Use our provider data hook
  const {
    providers,
    isLoading,
    pageInfo,
    handlePageChange,
    handleProviderSelect,
    canProceed
  } = useProviderData({
    selectedServices,
    selectedLocation,
    //@ts-expect-error
    activeService,
    ITEMS_PER_PAGE: layoutType === 'immersive'
      ? 4
      : (effectiveNodeData?.settings?.itemsPerRow || 3) * (effectiveNodeData?.settings?.numberRows || 2),
    setServiceProviderMap,
    serviceProviderMap
  });

  // Auto-advance to next unassigned service when current one gets auto-assigned (no providers)
  useEffect(() => {
    if (layoutType !== 'immersive' || !activeService) return;
    const mapped = serviceProviderMap[activeService];
    if (mapped && 'autoAssigned' in mapped) {
      const next = selectedServices.find(s => s.id !== activeService && !serviceProviderMap[s.id]);
      if (next) setActiveService(next.id);
    }
  }, [serviceProviderMap, activeService, selectedServices, layoutType]);

  // Handle selection (using the prop callback)
  const handleSelect = (provider: Provider) => {
    handleProviderSelect(provider);
    externalSetSelectedProvider(provider);
  };

  // Immersive: select provider then auto-advance to next unassigned service
  const handleImmersiveSelect = (provider: Provider) => {
    handleProviderSelect(provider);
    externalSetSelectedProvider(provider);
    const next = selectedServices.find(s => s.id !== activeService && !serviceProviderMap[s.id]);
    if (next) setActiveService(next.id);
  };

  // Generate dynamic pagination title
  const getPaginationTitle = () => {
    if (!activeService) return t('providers.chooseProvider'); // CHANGED: "Choose Provider"

    const activeServiceObj = selectedServices?.find(s => s.id === activeService);
    if (!activeServiceObj) return t('providers.chooseProvider'); // CHANGED: "Choose Provider"

    // Use a dynamic translation with service name
    return t('providers.chooseServiceProvider', { serviceName: activeServiceObj.name });
  };

  if (layoutType === 'immersive') {
    return (
      <ImmersiveProviderDisplay
        providers={providers}
        selectedServices={selectedServices}
        activeService={activeService}
        serviceProviderMap={serviceProviderMap}
        onProviderSelect={handleImmersiveSelect}
        onNext={onNext}
        canProceed={canProceed}
        isLoading={isLoading}
        hasNextPage={pageInfo?.hasNextPage ?? false}
        hasPrevPage={pageInfo?.hasPreviousPage ?? false}
        onNextPage={() => handlePageChange('next')}
        onPrevPage={() => handlePageChange('previous')}
      />
    );
  }

  return (
    <motion.div
      className="lp-max-w-7xl lp-mx-auto lp-space-y-6 lp-relative lp-overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <SelectionHeader
        selectedServices={selectedServices}
         //@ts-expect-error
        serviceProviderMap={serviceProviderMap}
         //@ts-expect-error
        activeService={activeService}
        setActiveService={setActiveService}
      />

      {isLoading ? (
        <div className="lp-flex lp-items-center lp-justify-center lp-h-48">
          <LoadingComponent />
        </div>
      ) : (
        <motion.div
          className="lp-space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Pagination
            pageInfo={pageInfo}
            handlePageChange={handlePageChange}
            title={getPaginationTitle()}
            Icon={Users}
            showNavigation={true}
          />

          {providers.length === 0 ? (
            <NoProvidersMessage colors={colors} />
          ) : (
            <ProviderDisplay
              providers={providers}
              selectedProviders={externalSelectedProvider === null ?
                externalSelectedProviders :
                 //@ts-expect-error
                (serviceProviderMap[activeService] || externalSelectedProvider)}
              onProviderClick={handleSelect}
              isClickable={true}
              nodeData={effectiveNodeData}
            />
          )}

          <FooterNavigation
            onNext={onNext}
            onPrev={onPrev}
            disabled={!canProceed}
          />
        </motion.div>
      )}
    </motion.div>
  );
};

export default ProviderStep;
